import { cn } from "../../lib/utils";

// variant controls the leading chip:
//   "delta"    — "+N pts · <action>" (scored users; truthful — moving the
//                factor moves the score).
//   "unlocks"  — "Unlocks scoring · <action>" (cap-resolving goal for a
//                capped user; "+N pts" would imply movement on a nil
//                score, which is the DNQ false-promise pattern).
//   "set-plan" — "Set · <action>" (Goal.kind === "set_plan"; state-change
//                prompt, no points reward — routes to the plan editor).
//   "none"     — action-only label (cap-shadowed non-cap goal — invitation
//                stays, but no "+N pts" promise on a capped headline).
export type SharpenPillVariant = "delta" | "unlocks" | "set-plan" | "none";

export interface SharpenPillProps {
  delta: number;
  action: string;
  onClick?: () => void;
  className?: string;
  variant?: SharpenPillVariant;
}

export function SharpenPill({
  delta,
  action,
  onClick,
  className,
  variant = "delta",
}: SharpenPillProps) {
  const leading =
    variant === "unlocks" ? (
      <>
        <span className="font-semibold uppercase tracking-[0.04em]">
          Unlocks scoring
        </span>
        <span className="opacity-60" aria-hidden="true">·</span>
      </>
    ) : variant === "set-plan" ? (
      <>
        <span className="font-semibold uppercase tracking-[0.04em]">Set</span>
        <span className="opacity-60" aria-hidden="true">·</span>
      </>
    ) : variant === "delta" ? (
      <>
        <span className="font-semibold tabular-nums">+{delta} pts</span>
        <span className="opacity-60" aria-hidden="true">·</span>
      </>
    ) : null;
  const content = (
    <>
      {leading}
      <span>{action}</span>
    </>
  );
  const baseClass = cn(
    "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs",
    variant === "unlocks" && "border-warning/40 bg-warning/10",
    variant === "set-plan" && "border-primary/30 bg-primary/10 text-primary",
    className,
  );
  if (onClick) {
    return (
      <button
        type="button"
        className={cn(baseClass, "transition hover:bg-accent")}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }
  return <span className={baseClass}>{content}</span>;
}
